// Menus API client (MOD-03, config-tier) — çok-seviye menü + öğe ağacı.
//
// Uçlar `/api/v1/menus/...` altında ve `ActionResponse<T>` döndürür. Tümü
// `web.manage` policy'siyle korunur. İş kuralları BE'de zorlanır:
//   INVALID_LOCATION / INVALID_LINK_TYPE / PARENT_MENU_MISMATCH / MENU_CYCLE /
//   LANGUAGE_INACTIVE.
//
// Publish iki-durum toggle'dır: POST /{id}/publish?publish=bool (Draft↔Published).
// Öğe create/update/reorder BooleanActionResponse döndürür → çağıran menüyü
// yeniden çeker. Menü create ise tam DTO döndürür.

import { http, unwrap, unwrapList, type ActionResponse } from './http';
import {
  normalizeConfigStatus,
  type CreateMenuItemRequest,
  type CreateMenuRequest,
  type Menu,
  type MenuCreateInput,
  type MenuDtoWire,
  type MenuItemCreateInput,
  type MenuItemDtoWire,
  type MenuItemNode,
  type MenuItemUpdateInput,
  type MenuLinkType,
  type MenuListItem,
  type MenuListItemDtoWire,
  type MenuListQuery,
  type MenuReorderItemWire,
  type MenuReorderNode,
  type MenuUpdateInput,
  type UpdateMenuItemRequest,
  type UpdateMenuRequest,
} from '@features/menus/types';

const BASE = '/api/v1/menus';

function mapListItem(d: MenuListItemDtoWire): MenuListItem {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    location: d.location ?? '',
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    itemCount: d.itemCount ?? 0,
    updatedAt: d.updatedAt ?? '',
  };
}

function mapItem(d: MenuItemDtoWire): MenuItemNode {
  return {
    id: d.id ?? '',
    menuId: d.menuId ?? '',
    parentId: d.parentId ?? null,
    label: d.label ?? '',
    linkType: (d.linkType ?? 'url') as MenuLinkType,
    url: d.url ?? null,
    contentSlug: d.contentSlug ?? null,
    target: d.target ?? null,
    order: d.order ?? 0,
    isActive: d.isActive ?? false,
    children: (d.children ?? []).map(mapItem),
  };
}

function mapMenu(d: MenuDtoWire): Menu {
  return {
    id: d.id ?? '',
    name: d.name ?? '',
    location: d.location ?? '',
    languageId: d.languageId ?? '',
    groupId: d.groupId ?? '',
    status: normalizeConfigStatus(d.status),
    variantLanguageCodes: d.variantLanguageCodes ?? [],
    items: (d.items ?? []).map(mapItem),
  };
}

export const menusApi = {
  /** Menüleri listeler (config-tier; hepsi tek sayfada). */
  async list(query: MenuListQuery = {}): Promise<MenuListItem[]> {
    const params: Record<string, string | number> = { page: 1, pageSize: 200 };
    if (query.location) params.location = query.location;
    if (query.languageId) params.languageId = query.languageId;
    const res = await http.get<ActionResponse<MenuListItemDtoWire>>(BASE, { params });
    const { items } = unwrapList(res);
    return items.map(mapListItem);
  },

  /** Menü detayı — öğe ağacıyla birlikte. */
  async get(id: string): Promise<Menu> {
    const res = await http.get<ActionResponse<MenuDtoWire>>(`${BASE}/${id}`);
    return mapMenu(unwrap(res));
  },

  /** POST → tam MenuDto döner. */
  async create(input: MenuCreateInput): Promise<Menu> {
    const body: CreateMenuRequest = {
      name: input.name,
      location: input.location,
      ...(input.languageId ? { languageId: input.languageId } : {}),
      ...(input.groupId ? { groupId: input.groupId } : {}),
    };
    const res = await http.post<ActionResponse<MenuDtoWire>>(BASE, body);
    return mapMenu(unwrap(res));
  },

  /** PUT → Boolean; güncel menüyü geri çeker. */
  async update(id: string, input: MenuUpdateInput): Promise<Menu> {
    const body: UpdateMenuRequest = { name: input.name, location: input.location };
    unwrap(await http.put<ActionResponse<boolean>>(`${BASE}/${id}`, body));
    return menusApi.get(id);
  },

  async remove(id: string): Promise<void> {
    unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/${id}`));
  },

  /** Publish toggle — iki-durum (Draft↔Published). */
  async setPublished(id: string, publish: boolean): Promise<void> {
    unwrap(
      await http.post<ActionResponse<boolean>>(`${BASE}/${id}/publish`, null, {
        params: { publish },
      }),
    );
  },

  items: {
    /** Yeni öğe ekler (parentId ile kök/alt). Boolean döner. */
    async create(menuId: string, input: MenuItemCreateInput): Promise<void> {
      const body: CreateMenuItemRequest = {
        label: input.label,
        linkType: input.linkType,
        parentId: input.parentId ?? null,
        url: input.url ?? null,
        contentSlug: input.contentSlug ?? null,
        target: input.target ?? null,
        order: input.order ?? null,
        isActive: input.isActive,
      };
      unwrap(await http.post<ActionResponse<boolean>>(`${BASE}/${menuId}/items`, body));
    },

    /** Öğeyi günceller (parent/order hariç). Boolean döner. */
    async update(itemId: string, input: MenuItemUpdateInput): Promise<void> {
      const body: UpdateMenuItemRequest = {
        label: input.label,
        linkType: input.linkType,
        url: input.url ?? null,
        contentSlug: input.contentSlug ?? null,
        target: input.target ?? null,
        isActive: input.isActive,
      };
      unwrap(await http.put<ActionResponse<boolean>>(`${BASE}/items/${itemId}`, body));
    },

    async remove(itemId: string): Promise<void> {
      unwrap(await http.delete<ActionResponse<boolean>>(`${BASE}/items/${itemId}`));
    },

    /** Ağacı yeniden sıralar (sıra + re-parent). BE cycle guard'a güvenir. */
    async reorder(menuId: string, nodes: MenuReorderNode[]): Promise<void> {
      const items: MenuReorderItemWire[] = nodes.map((n) => ({
        id: n.id,
        order: n.order,
        parentId: n.parentId,
      }));
      unwrap(
        await http.put<ActionResponse<boolean>>(`${BASE}/${menuId}/items/reorder`, { items }),
      );
    },
  },
};
